// src/service/auth.js

import Cookies from "js-cookie";
import {
  axiosAssessmentInstance,
  axiosAuthInstance,
  axiosLoginInstance,
} from "../AxiosConfiguration";

export const storeTokens = async (response) => {
  if (!response) {
    console.error("idToken or refreshToken is undefined:", response);
  } else {
    const {
      idToken,
      refreshToken,
      localId,
      user_id,
      userId,
      profilePicture,
      expiresIn,
    } = response || {};
    if (idToken && refreshToken) {
      const expiryTime = 3600;
      // Set tokens in cookies if available
      // Cookies.set('idtoken', idToken, { expires: 7, secure: true, sameSite: 'Strict' });  // Cookie will expire in 7 days
      // Cookies.set('refreshtoken', refreshToken, { expires: 7, secure: true, sameSite: 'Strict' });
      Cookies.set("idtoken", idToken, {
        secure: true,
      }); // Cookie will expire in 7 days
      Cookies.set("refreshtoken", refreshToken, {
        secure: true,
      });
      Cookies.set("localId", localId || user_id || userId);
      if (profilePicture) Cookies.set("profilePicture", profilePicture);
    } else {
      console.error("idToken or refreshToken is undefined:", {
        idToken,
        refreshToken,
      });
    }
  }
};


// Email OTP sign-in: verify OTP
// `shareToken` (optional) is the Share Aris invite token carried from the invite
// link; the backend uses it to activate a Companion Membership / Aris Pass on signup.
export const verifyEmailSignInOtp = async (email, otp, shareToken) => {
  try {
    const body = { email, otp };
    if (shareToken) body.share_token = shareToken;
    const response = await axiosLoginInstance.post("/auth/v2/otp-email-sign-in", body);
    if (response.status === 200 || response.status === 231) {
      const { idToken, refreshToken, localId } = response.data.payload || {};
      if (idToken) Cookies.set("idtoken", idToken);
      if (refreshToken) Cookies.set("refreshtoken", refreshToken);
      if (localId) Cookies.set("localId", localId);
      const deviceId = response.headers["Device_id"] || response.headers["device_id"];
      if (deviceId) Cookies.set("Device_id", deviceId);
    }
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw mapCorporateAuthError({
        ...error.response.data,
        status: error.response.status,
      });
    }
    throw {
      status: error.response?.status || 500,
      message: error.message || "Failed to verify OTP",
      payload: {},
    };
  }
};


// V2 Unified Authentication APIs
// Authenticate with email - handles both login and signup
export const authenticateWithEmail = async (email, timezone) => {
  try {
    const payload = { 
      email: email,
      timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    const response = await axiosLoginInstance.post("/auth/v2/authenticate-email", payload);
    return response.data;
  } catch (error) {
    if (error.response?.data) throw error.response.data;
    throw {
      status: error.response?.status || 500,
      message: error.message || "Failed to authenticate with email",
      payload: {},
    };
  }
};

// Authenticate with phone - handles both login and signup
export const authenticateWithPhone = async (contact, country_code, timezone) => {
  try {
    const payload = { 
      contact,
      country_code,
      timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    const response = await axiosLoginInstance.post("/auth/v2/authenticate-phone", payload);
    return response.data;
  } catch (error) {
    if (error.response?.data) throw error.response.data;
    throw {
      status: error.response?.status || 500,
      message: error.message || "Failed to authenticate with phone",
      payload: {},
    };
  }
};

// Phone signup: verify OTP
// `shareToken` (optional) is the Share Aris invite token carried from the invite
// link; the backend uses it to activate a Companion Membership / Aris Pass on signup.
export const verifySignupPhone = async (country_code, contact, otp, shareToken) => {
  try {
    const body = { country_code, contact, otp };
    if (shareToken) body.share_token = shareToken;
    const response = await axiosLoginInstance.post("/auth/v2/otp-phone-sign-in", body);
    if (response.status === 200 || response.status === 231) {
      const { idToken, refreshToken, localId } = response.data?.payload || {};
      if (idToken) Cookies.set("idtoken", idToken);
      if (refreshToken) Cookies.set("refreshtoken", refreshToken);
      if (localId) Cookies.set("localId", localId);
      const deviceId = response.headers?.["Device_id"] || response.headers?.["device_id"];
      if (deviceId) Cookies.set("Device_id", deviceId);
    }
    return response.data;
  } catch (error) {
    if (error.response?.data) throw error.response.data;
    throw {
      status: error.response?.status || 500,
      message: error.message || "Failed to verify signup OTP",
      payload: {},
    };
  }
};

export const generateUploadURL = async (file_name, file_type) => {
  try {
    const response = await axiosAuthInstance.post(
      `/auth/v2/upload/presigned-url?file_name=${file_name}&file_type=${file_type}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${Cookies.get("idtoken")}`,
        },
      }
    );

    return response.data.payload;
  } catch (error) {
    console.error("Error generating upload URL:", error);
    throw error;
  }
};

export const uploadProfilePhoto = async (key) => {
  try {
    const response = await axiosAuthInstance.post(`/auth/v2/upload/profile-url`, {
      key: key,
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading profile photo:", error);
    throw error;
  }
};


export const loginWithPhone = async (country_code, contact) => {
  try {
    const response = await axiosLoginInstance.post(
      "/auth/v2/sign-in-number",
      {
        country_code,
        contact,
      }
    );
    if (response.status === 200 || response.status === 231) {
      return response.data;
    }
  } catch (error) {
    console.error(
      "Login with phone failed:",
      error.response?.data || error.message
    );

    // Preserve the original error structure
    if (error.response?.data) {
      // If the error has the specific structure we're looking for
      if (
        error.response.data.status === 400 &&
        error.response.data.message === "Maximum number of devices reached"
      ) {
        throw error.response.data; // Throw the entire error object
      } else if (
        error.response.data.status === 421 &&
        error.response.data.message ===
        "User is deleted, Please create a new account"
      ) {
        throw error.response.data; // Throw the entire error object
      } else if (
        error.response.data.status === 322 &&
        error.response.data.message === "Your subscription is pending renewal"
      ) {
        throw error.response.data; // Throw the entire error object
      }

      // For other structured errors
      throw {
        status: error.response.status,
        message: error.response.data.message || "Incorrect email or password",
        payload: error.response.data.payload || {},
      };
    }

    // For network errors or other unstructured errors
    throw {
      status: error.response?.status || 500,
      message: error.message || "An error occurred",
      payload: {},
    };
  }
};




const clearAllCookies = () => {
  // Get all cookies
  const allCookies = Cookies.get();

  // Loop through all cookies and remove them
  Object.keys(allCookies).forEach((cookie) => {
    Cookies.remove(cookie);
  });
};

export const signOut = async () => {
  try {
    // Retrieve Device_id from cookies
    const deviceId = Cookies.get("Device_id");

    // Make the signout request with Device_id in headers (use axios config as 3rd arg)
    const response = await axiosAuthInstance.post(
      "/auth/v2/logout",
      {},
      {
        headers: {
          Device_id: deviceId,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error signing out:", error.response || error.message);
    throw error;
  } finally {
    // Always clear client-side auth state to avoid stale sessions
    try {
      clearAllCookies();
      localStorage.clear();
    } catch (_) {
      // no-op
    }
  }
};

export const updateBasicDetails = async (details) => {
  try {
    const response = await axiosAuthInstance.put("/auth/v2/user/update-basic-details", {
      first_name: details.firstName,
      last_name: details.lastName,
      date_of_birth: details.dateOfBirth,
      gender: details.gender,
      pronoun: details.pronoun,
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to update basic details:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const handleFaith = async (faith) => {
  try {
    const response = await axiosAuthInstance.post(`/auth/v2/user/faith-details?`, { faith });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to update faith",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const getRefreshToken = () => {
  return Cookies.get("refresh_token");
};

export const clearTokens = () => {
  Cookies.remove("idtoken");
  Cookies.remove("refreshtoken");
};

// Check whether a user exists by email or phone
// Expected backend: POST /auth/v2/user/exists with payload { email? , country_code?, contact? }
// Returns: { exists: boolean }
export const checkUserExists = async ({ email, country_code, contact }) => {
  try {
    const payload = {};
    if (email) payload.email = email;
    if (country_code && contact) {
      payload.country_code = country_code;
      payload.contact = contact;
    }
    const res = await axiosAuthInstance.post("/auth/v2/user/exists", payload);
    const raw = res?.data || {};
    // Try common shapes
    const exists = Boolean(
      raw?.exists ?? raw?.payload?.exists ?? raw?.data?.exists ?? raw?.payload === true
    );
    return { exists };
  } catch (e) {
    // If endpoint not available yet, default to not exists
    return { exists: false };
  }
};

export const manageSession = async (email) => {
  try {
    const response = await axiosAuthInstance.post("/auth/manage_session", {
      email,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting session:", error.response || error.message);
    throw error;
  }
};

// Google Sign-In
export const signInWithGoogle = async (googleData) => {
  try {
    const response = await axiosLoginInstance.post("/auth/v2/google/sign-in", {
      id_token: googleData.id_token,
      access_token: googleData.access_token,
      user_id: googleData.user_id,
      email: googleData.email,
      email_verified: googleData.email_verified,
      first_name: googleData.first_name,
      last_name: googleData.last_name,
      profile_picture: googleData.profile_picture,
      timezone: googleData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    if (response.status === 200 || response.status === 231 || response.status === 323) {
      const { idToken, refreshToken, localId } = response.data.payload || {};
      if (idToken) Cookies.set("idtoken", idToken);
      if (refreshToken) Cookies.set("refreshtoken", refreshToken);
      if (localId) Cookies.set("localId", localId);
      
      const deviceId = response.headers["Device_id"] || response.headers["device_id"];
      if (deviceId) Cookies.set("Device_id", deviceId);
    }
    
    return response.data;
  } catch (error) {
    console.error("Google sign-in failed:", error.response?.data || error.message);
    
    if (error.response?.data) {
      if (
        error.response.data.status === 400 &&
        error.response.data.message === "Maximum number of devices reached"
      ) {
        throw error.response.data;
      } else if (
        error.response.data.status === 322 &&
        error.response.data.message === "Your subscription is pending renewal"
      ) {
        throw error.response.data;
      }
      
      throw {
        status: error.response.status,
        message: error.response.data.message || "Google sign-in failed",
        payload: error.response.data.payload || {},
      };
    }
    
    throw {
      status: error.response?.status || 500,
      message: error.message || "An error occurred during Google sign-in",
      payload: {},
    };
  }
};

// Apple Sign-In
export const signInWithApple = async (appleData) => {
  try {
    const response = await axiosLoginInstance.post("/auth/v2/apple/sign-in", {
      identity_token: appleData.identity_token,
      authorization_code: appleData.authorization_code,
      user_identifier: appleData.user_identifier,
      email: appleData.email,
      first_name: appleData.first_name,
      last_name: appleData.last_name,
      is_private_email: appleData.is_private_email || false,
      timezone: appleData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    if (response.status === 200 || response.status === 231 || response.status === 323) {
      const { idToken, refreshToken, localId } = response.data.payload || {};
      if (idToken) Cookies.set("idtoken", idToken);
      if (refreshToken) Cookies.set("refreshtoken", refreshToken);
      if (localId) Cookies.set("localId", localId);
      
      const deviceId = response.headers["Device_id"] || response.headers["device_id"];
      if (deviceId) Cookies.set("Device_id", deviceId);
    }
    
    return response.data;
  } catch (error) {
    console.error("Apple sign-in failed:", error.response?.data || error.message);
    
    if (error.response?.data) {
      if (
        error.response.data.status === 400 &&
        error.response.data.message === "Maximum number of devices reached"
      ) {
        throw error.response.data;
      } else if (
        error.response.data.status === 322 &&
        error.response.data.message === "Your subscription is pending renewal"
      ) {
        throw error.response.data;
      }
      
      throw {
        status: error.response.status,
        message: error.response.data.message || "Apple sign-in failed",
        payload: error.response.data.payload || {},
      };
    }
    
    throw {
      status: error.response?.status || 500,
      message: error.message || "An error occurred during Apple sign-in",
      payload: {},
    };
  }
};

// ─── Corporate login error mapping ─────────────────────────────────────────

/**
 * Maps corporate-specific auth errors to user-friendly messages per spec §5.5.
 * Returns the original error untouched if it isn't one of the known codes,
 * so existing login UI behavior is preserved for everything else.
 *
 * @param {*} error - the error envelope thrown by an auth.js function
 * @returns {*} possibly-rewritten error
 */
export const mapCorporateAuthError = (error) => {
  if (!error || typeof error !== "object") return error;
  const status = error.status_code || error.status;
  const code = error.payload?.code || error.code;
  const messageRaw = error.message || "";

  if (
    (status === 401 && code === "work_email_no_longer_valid") ||
    /work_email_no_longer_valid/i.test(messageRaw)
  ) {
    return {
      ...error,
      message:
        "This work email is no longer active. Please log in with your personal email.",
      code: "work_email_no_longer_valid",
    };
  }
  if (
    (status === 403 && code === "corporate_account_deactivated") ||
    /corporate_account_deactivated/i.test(messageRaw)
  ) {
    return {
      ...error,
      message:
        "Your corporate account has been suspended. Please contact your administrator.",
      code: "corporate_account_deactivated",
    };
  }
  return error;
};

// ─── Corporate signup (auth-lambda) ────────────────────────────────────────

/**
 * Send a 6-digit OTP to the corporate member's work email. The backend still
 * records the personal_email on the OTP session even though we no longer
 * verify it client-side. Rate-limited to 3/hour by auth-lambda — surface 429
 * as a user-friendly "wait an hour" message.
 *
 * @param {{member_id:string, work_email:string, personal_email:string}} body
 * @returns {Promise<{otp_session_id:string}>}
 */
export const corporateSignupOtp = async (body) => {
  try {
    const response = await axiosLoginInstance.post(
      "/auth/v2/corporate/signup-otp",
      body
    );
    return response.data?.payload;
  } catch (error) {
    if (error.response?.data) throw error.response.data;
    throw error;
  }
};

/**
 * Verify the work-email OTP and create the user account. On success, persists
 * tokens the same way the regular login flow does so subsequent navigations
 * recognize the user as authenticated.
 *
 * @param {{otp_session_id:string, otp:string, password?:string}} body
 * @returns {Promise<{access_token:string, refresh_token:string, user_id:string, account_type:string, corporate_id:string}>}
 */
export const corporateSignupVerify = async (body) => {
  try {
    const response = await axiosLoginInstance.post(
      "/auth/v2/corporate/signup-verify",
      body
    );
    const payload = response.data?.payload || {};
    const { access_token, refresh_token, user_id } = payload;
    if (access_token) Cookies.set("idtoken", access_token, { secure: true });
    if (refresh_token) Cookies.set("refreshtoken", refresh_token, { secure: true });
    if (user_id) Cookies.set("localId", user_id);
    const deviceId =
      response.headers?.["Device_id"] || response.headers?.["device_id"];
    if (deviceId) Cookies.set("Device_id", deviceId);
    return payload;
  } catch (error) {
    if (error.response?.data) throw error.response.data;
    throw error;
  }
};

// Send app download link to phone number
export const sendAppLink = async (phone_number) => {
  try {
    const response = await axiosAssessmentInstance.post("/api/v2/qr/send-app-link", {
      phone_number,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to send app link:", error.response?.data || error.message);
    
    if (error.response?.data) {
      throw {
        status: error.response.status,
        message: error.response.data.message || "Failed to send app link",
        payload: error.response.data.payload || {},
      };
    }
    
    throw {
      status: error.response?.status || 500,
      message: error.message || "An error occurred while sending app link",
      payload: {},
    };
  }
};
